/**
 * AI Storybook — a tagged face's skin tone and hair colour, read from the
 * parent's photo in the browser and named as one of the appearance chips, so
 * the builder can SUGGEST a person's chips from their photo -- a "Use these"
 * button the parent has to click. Nothing is ever chosen or filled from a
 * photo (owner, 2026-09-28): even refusing on weak evidence, the estimator
 * still gives confident wrong chips in ordinary photos (coloured light on the
 * face, a knit hat, stubble, phone HDR; docs/storybook-identity-lock.md §12).
 *
 * The twin of FaceShot-ChopShop-web/services/storybook/colour.js: the same
 * constants, references and steps, line for line. Both run the same fixtures
 * and scenarios (tests/fixtures/storybook-colour), and storybook-colour.test.ts
 * runs both and compares every answer. Change one, change the other.
 *
 * Why it exists, how it works and how it was calibrated: the server file's
 * header and docs/storybook-identity-lock.md §12. In short: the worker's
 * identity lock sees faces in grayscale, so skin and hair colour in the
 * drawings come only from these chips; a confident wrong chip is worse than
 * none, so every step refuses (null + a reason) rather than guesses -- a
 * face cut by the photo's edge, dim or bright light beyond a small
 * correction, a face in shade, a colour cast, a scalp, a hat, sparse hair, an
 * answer that changes when the box is 7 % smaller or bigger (step 9) --
 * and a chip is named only at STORYBOOK_COLOUR_PICK_CONFIDENCE.
 *
 * Nothing here leaves the page, and nothing is kept.
 */

import type { StorybookFaceBox } from '@/lib/storybook-api';
import { isBaldHairStyle } from '@/lib/storybook-utils';

export const STORYBOOK_SKIN_TONES = ['very light', 'light', 'light-medium', 'medium', 'tan', 'brown', 'deep brown'] as const;
export const STORYBOOK_HAIR_COLORS = [
  'black',
  'dark brown',
  'brown',
  'light brown',
  'blonde',
  'strawberry blonde',
  'red',
  'auburn',
  'gray',
  'white',
] as const;

type Lab = [number, number, number];
type Rgb = [number, number, number];

/** Lit skin, exposure-corrected, as a photo shows it: CIE Lab (D65) [L, a, b]. */
export const SKIN_REFERENCES: Readonly<Record<string, Readonly<Lab>>> = Object.freeze({
  'very light': Object.freeze([77, 16, 14] as Lab),
  light: Object.freeze([73, 19.1, 20.5] as Lab),
  'light-medium': Object.freeze([69.5, 16.2, 21.6] as Lab),
  medium: Object.freeze([66.5, 20, 27.5] as Lab),
  tan: Object.freeze([63, 16.5, 29.7] as Lab),
  brown: Object.freeze([56, 16.5, 28.6] as Lab),
  'deep brown': Object.freeze([45, 15.1, 22.4] as Lab),
});

/** The median of the hair above a face in a photo, shading included: CIE Lab (D65) [L, a, b]. */
export const HAIR_REFERENCES: Readonly<Record<string, Readonly<Lab>>> = Object.freeze({
  black: Object.freeze([7, 0, 0] as Lab),
  'dark brown': Object.freeze([15, 3, 4] as Lab),
  brown: Object.freeze([18, 8, 11] as Lab),
  'light brown': Object.freeze([33, 9, 17] as Lab),
  blonde: Object.freeze([56, 4, 26] as Lab),
  'strawberry blonde': Object.freeze([50, 14, 25] as Lab),
  red: Object.freeze([35, 24, 26] as Lab),
  auburn: Object.freeze([24, 15, 13] as Lab),
  gray: Object.freeze([52, 0, 2] as Lab),
  white: Object.freeze([78, 0, 3] as Lab),
});

type Region = readonly [number, number, number, number];

export const COLOUR = Object.freeze({
  /** Cells across the face box. */
  grid: 48,
  /** The analysis window around the face box, in box widths (left/right) and heights (top/bottom). */
  window: Object.freeze({ left: 0.5, right: 0.5, top: 0.6, bottom: 0.1 }),
  /** [u0, v0, u1, v1] in face-box units (0..1 is the box): the forehead, then each cheek. */
  skinRegions: Object.freeze([
    Object.freeze([0.3, 0.06, 0.7, 0.24] as const),
    Object.freeze([0.14, 0.5, 0.36, 0.7] as const),
    Object.freeze([0.64, 0.5, 0.86, 0.7] as const),
  ]) as readonly Region[],
  /** Above the head, then beside each temple. */
  hairRegions: Object.freeze([
    Object.freeze([0.1, -0.28, 0.9, 0.03] as const),
    Object.freeze([-0.1, 0.0, 0.06, 0.3] as const),
    Object.freeze([0.94, 0.0, 1.1, 0.3] as const),
  ]) as readonly Region[],
  /** The band just above the forehead that must be mostly hair. */
  hairTop: Object.freeze([0.2, -0.25, 0.8, -0.02] as const) as Region,
  /** Eyes and brows: darker than the skin in any face. */
  eyeBand: Object.freeze([0.18, 0.3, 0.82, 0.5] as const) as Region,
  /** Radius of the face oval (box units, centred) whose highlights say how bright the photo is. */
  faceOval: 0.45,
  /** Radius of the ellipse (box units, centred) outside which cells are the face's surroundings. */
  faceEllipse: 0.62,
  /** Cells on the window's side edges count as background above this box height only (below: shoulders, long hair). */
  borderMaxV: 0.35,
  skinGate: Object.freeze({ minL: 12, maxL: 96, minA: 2, minB: 3, minHue: 20, maxHue: 85, minChroma: 5, maxChroma: 45 }),
  /** The gated skin cells kept, as lightness quantiles: shadows under, shine over. */
  litBand: Object.freeze([0.35, 0.9] as const),
  /** A face under this many photo pixels on its short side is not read (half a pixel of rounding forgiven). */
  minFacePx: 48,
  minSkinCells: 60,
  /** Of the forehead and cheek cells, at least this share must be skin. */
  minSkinShare: 0.6,
  /** Is it a face: eyes/brows (their 10th lightness percentile) this much darker than the skin; forehead and cheeks within this hue (degrees) and this lightness spread. */
  face: Object.freeze({ eyesDarker: 12, partsHueGap: 12, partsSpread: 28 }),
  minHairCells: 40,
  /** A box closer than this (box sizes) to the photo's edge is a face the edge cuts. */
  edgeMargin: 0.04,
  /** Room the hair needs inside the photo, in box sizes: above the box, and beside it. */
  hairRoom: Object.freeze({ top: 0.3, side: 0.12 }),
  /**
   * Highlights: this quantile of the face oval's L, brought toward `target`
   * with `strength` of the gap; applied only up to `brighten` / `darken`
   * stops (`smallStops` for a face under `smallFacePx`), refused beyond.
   */
  exposure: Object.freeze({ percentile: 0.98, target: 81, strength: 0.7, brighten: 0.3, darken: 0.4, smallFacePx: 96, smallStops: 0.2 }),
  /** Too bright to read: the lit skin's L above this, or more than this share of the skin regions' pixels clipped (a channel at 250+). */
  blownOut: Object.freeze({ maxSkinL: 92, clippedShare: 0.25 }),
  /** In shade: the surroundings' lightness (this quantile) over the face's highlights by more than maxGap; a forehead more than foreheadShadow under both cheeks. */
  shade: Object.freeze({ outsideQuantile: 0.9, maxGap: 12, foreheadShadow: 6 }),
  /** White balance: the brightest `share` of the surroundings (at least minL, at least minCells of them) within maxChroma of grey; else the window's grey world within grayWorldMaxChroma. */
  cast: Object.freeze({ share: 0.15, minL: 55, minCells: 30, maxChroma: 8, grayWorldMaxChroma: 14 }),
  /** CIE94 (textile) distance at which a skin colour stops looking like a reference at all. */
  skinMaxDistance: 6,
  /** CIE76 distance, the same for hair. */
  hairMaxDistance: 14,
  /** A hair cell is at least this far (ΔE) from the border colour. */
  backgroundDistance: 12,
  /** A hair cell differs from the skin by at least minDistance (ΔE), and is darker, more saturated, of another hue, or grey. */
  hairVsSkin: Object.freeze({ minDistance: 12, darker: 8, moreChroma: 6, hueTurn: 15, hueMinChroma: 10, greyChroma: 8, greyerBy: 10 }),
  hairGates: Object.freeze({
    greyMaxChroma: 9,
    greyMinL: 35,
    warmMinA: 10,
    warmMaxHue: 65,
    warmMinChroma: 12,
    blondeMinL: 38,
    blondeMinHue: 60,
    blondeMinChroma: 12,
  }),
  /** Of the band above the forehead, at least this share must be hair (sample hair: 0.65 and up; a box on the brows and eyes: 0.57). */
  minHairCover: 0.6,
  /** A scalp: the "hair" within this rgb chromaticity of the skin, and more than this share of its luminance. */
  scalp: Object.freeze({ maxChromaticity: 0.035, minYRatio: 0.3 }),
  /** Hair is at least this much rougher (cell-to-cell luminance contrast) than the skin of the same face. */
  texture: Object.freeze({ minRatio: 3 }),
  /** A hat: over minColumns of the forehead columns, a straight lower edge (fit residual, box heights) with a colour jump of minJump (ΔE), under a band smoother than maxTexture. */
  hat: Object.freeze({ maxTexture: 6, minJump: 20, maxResidual: 0.03, minColumns: 0.7 }),
  /** A field's support stops growing at this multiple of its minimum cell count. */
  fullSupport: 2,
  /** Box stability: a named chip is read again from the box scaled by each of these about its centre; at least minAgree must name it, none another chip. */
  stability: Object.freeze({ scales: Object.freeze([0.93, 1.07]) as readonly number[], minAgree: 1 }),
  /** THE confidence a chip needs to be named (and so offered by the builder as a suggestion). */
  pickConfidence: 0.6,
  /** Gray or white hair on a child needs this much. */
  childGreyConfidence: 0.99,
});

/** The one confidence a chip is named at (§12; the server twin's PICK_CONFIDENCE). */
export const STORYBOOK_COLOUR_PICK_CONFIDENCE = COLOUR.pickConfidence;

// ---------------------------------------------------------------------------
// Colour maths: sRGB (D65) -> linear -> XYZ -> CIE Lab
// ---------------------------------------------------------------------------
const SRGB_TO_LINEAR = new Float64Array(256);
for (let c = 0; c < 256; c += 1) {
  const v = c / 255;
  SRGB_TO_LINEAR[c] = v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}
const WHITE = [0.95047, 1, 1.08883];
const EPS = 216 / 24389;
const KAPPA = 24389 / 27;
const labF = (t: number) => (t > EPS ? Math.cbrt(t) : (KAPPA * t + 16) / 116);
const labFInv = (t: number) => (t ** 3 > EPS ? t ** 3 : (116 * t - 16) / KAPPA);

function xyzToLab(x: number, y: number, z: number): Lab {
  const fx = labF(x / WHITE[0]);
  const fy = labF(y / WHITE[1]);
  const fz = labF(z / WHITE[2]);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

function labToXyz(L: number, a: number, b: number): [number, number, number] {
  const fy = (L + 16) / 116;
  return [WHITE[0] * labFInv(fy + a / 500), WHITE[1] * labFInv(fy), WHITE[2] * labFInv(fy - b / 200)];
}

/** Linear-light RGB (0..1) -> CIE Lab. */
function linearToLab(r: number, g: number, b: number): Lab {
  return xyzToLab(
    0.4124564 * r + 0.3575761 * g + 0.1804375 * b,
    0.2126729 * r + 0.7151522 * g + 0.072175 * b,
    0.0193339 * r + 0.119192 * g + 0.9503041 * b,
  );
}

/** 8-bit sRGB -> CIE Lab. */
export function srgbToLab(r: number, g: number, b: number): Lab {
  return linearToLab(SRGB_TO_LINEAR[r], SRGB_TO_LINEAR[g], SRGB_TO_LINEAR[b]);
}

const chroma = (lab: Readonly<Lab>) => Math.hypot(lab[1], lab[2]);
/** Hue angle in degrees, 0..360. */
const hue = (lab: Readonly<Lab>) => ((Math.atan2(lab[2], lab[1]) * 180) / Math.PI + 360) % 360;
/** CIE76. */
const deltaE = (p: Readonly<Lab>, q: Readonly<Lab>) => Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]);
/** CIE94 with the textile weights (kL 2, K1 0.048, K2 0.014), `ref` the reference colour. */
export function deltaE94(lab: Readonly<Lab>, ref: Readonly<Lab>): number {
  const c1 = chroma(ref);
  const dC = chroma(lab) - c1;
  const dH2 = Math.max(0, (lab[1] - ref[1]) ** 2 + (lab[2] - ref[2]) ** 2 - dC ** 2);
  return Math.sqrt(((lab[0] - ref[0]) / 2) ** 2 + (dC / (1 + 0.048 * c1)) ** 2 + dH2 / (1 + 0.014 * c1) ** 2);
}
const hueGap = (h1: number, h2: number) => {
  const d = Math.abs(h1 - h2) % 360;
  return d > 180 ? 360 - d : d;
};
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const round = (v: number, n = 2) => Math.round(v * 10 ** n) / 10 ** n;

/** The p-quantile (0..1) of a list of numbers: nearest rank on the sorted list. */
function quantile(values: number[], p: number): number {
  if (!values.length) return NaN;
  const sorted = Float64Array.from(values).sort();
  return sorted[Math.min(sorted.length - 1, Math.max(0, Math.round((sorted.length - 1) * p)))];
}
const median = (values: number[]) => quantile(values, 0.5);

interface Cell {
  row: number;
  col: number;
  u: number;
  v: number;
  rgb: Rgb;
  lab: Lab;
  n: number;
  clippedShare: number;
  clipped: boolean;
  border: boolean;
}

interface Sampled {
  cells: Cell[];
  grid: Array<Cell | null>;
  cols: number;
  rows: number;
}

/** Component-wise median of cells' Lab colours. */
function medianLab(cells: Cell[]): Lab {
  return [0, 1, 2].map((k) => median(cells.map((c) => c.lab[k]))) as Lab;
}

/** Component-wise median of cells' linear RGB. */
function medianRgb(cells: Cell[]): Rgb {
  return [0, 1, 2].map((k) => median(cells.map((c) => c.rgb[k]))) as Rgb;
}

/** Relative luminance of linear RGB. */
const luminance = (rgb: Readonly<Rgb>) => 0.2126729 * rgb[0] + 0.7151522 * rgb[1] + 0.072175 * rgb[2];

/** rgb chromaticity: each channel's share of the three. */
function chromaticity(rgb: Readonly<Rgb>): Rgb {
  const sum = rgb[0] + rgb[1] + rgb[2] || 1;
  return [rgb[0] / sum, rgb[1] / sum, rgb[2] / sum];
}

/** A Lab colour with its light (XYZ) scaled by k, as a brighter or darker exposure would give. */
function exposeLab(lab: Lab, k: number): Lab {
  const [x, y, z] = labToXyz(lab[0], lab[1], lab[2]);
  return xyzToLab(x * k, y * k, z * k);
}

/** Luminance Y of a grey of lightness L. */
const lightnessToY = (L: number) => labToXyz(L, 0, 0)[1];

// ---------------------------------------------------------------------------
// Sampling
// ---------------------------------------------------------------------------

export interface StorybookColourWindow {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** 8-bit sRGB pixels, 3 or 4 channels (4 by default: a canvas's ImageData). */
export interface StorybookColourImage {
  data: ArrayLike<number>;
  width: number;
  height: number;
  channels?: number;
}

/** A box scaled by s about its centre. */
function scaleBox(box: StorybookFaceBox, s: number): StorybookFaceBox {
  const cx = (box[0] + box[2]) / 2;
  const cy = (box[1] + box[3]) / 2;
  const hw = ((box[2] - box[0]) * s) / 2;
  const hh = ((box[3] - box[1]) * s) / 2;
  return [cx - hw, cy - hh, cx + hw, cy + hh];
}

/** The pixel rectangle one read of a box averages: its window margins, stopped at the photo's edges. */
function windowOf(box: StorybookFaceBox, width: number, height: number): StorybookColourWindow {
  const bw = (box[2] - box[0]) * width;
  const bh = (box[3] - box[1]) * height;
  const left = Math.max(0, Math.floor(box[0] * width - COLOUR.window.left * bw));
  const top = Math.max(0, Math.floor(box[1] * height - COLOUR.window.top * bh));
  const right = Math.min(width, Math.ceil(box[2] * width + COLOUR.window.right * bw));
  const bottom = Math.min(height, Math.ceil(box[3] * height + COLOUR.window.bottom * bh));
  return { left, top, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

/**
 * The pixel rectangle the estimator reads for a face box (normalized
 * [x0, y0, x1, y1]) in a width x height image: the window of the largest
 * stability re-read, which holds every other. A caller that cuts exactly this
 * out and passes the box re-normalized to it (boxInWindow) gets the same
 * answer as one that passes the whole image: the window only ever stops at
 * the photo's own edges, so the distance from the box to them is kept.
 */
export function analysisWindow(box: StorybookFaceBox, width: number, height: number): StorybookColourWindow {
  return windowOf(scaleBox(box, Math.max(1, ...COLOUR.stability.scales)), width, height);
}

/** A face box, normalized to the whole image, re-normalized to a window cut out of it. */
export function boxInWindow(box: StorybookFaceBox, width: number, height: number, win: StorybookColourWindow): StorybookFaceBox {
  return [
    (box[0] * width - win.left) / win.width,
    (box[1] * height - win.top) / win.height,
    (box[2] * width - win.left) / win.width,
    (box[3] * height - win.top) / win.height,
  ];
}

/** The window averaged into cells, as a grid: u/v the cell's centre in face-box units, rgb its mean linear RGB, n its pixels, clippedShare those at 250+. */
function sampleCells(image: StorybookColourImage, box: StorybookFaceBox): Sampled {
  const { data, width, height } = image;
  const channels = image.channels || 4;
  const bx0 = box[0] * width;
  const by0 = box[1] * height;
  const bw = (box[2] - box[0]) * width;
  const bh = (box[3] - box[1]) * height;
  const win = windowOf(box, width, height);
  const size = Math.max(1, bw / COLOUR.grid);
  const cols = Math.max(1, Math.ceil(win.width / size));
  const rows = Math.max(1, Math.ceil(win.height / size));
  const sum = new Float64Array(cols * rows * 3);
  const count = new Uint32Array(cols * rows);
  const clipped = new Uint32Array(cols * rows);
  const colOf = new Int32Array(win.width);
  for (let x = 0; x < win.width; x += 1) colOf[x] = Math.min(cols - 1, Math.floor(x / size));
  for (let y = 0; y < win.height; y += 1) {
    const rowBase = Math.min(rows - 1, Math.floor(y / size)) * cols;
    let i = ((win.top + y) * width + win.left) * channels;
    for (let x = 0; x < win.width; x += 1, i += channels) {
      const cell = rowBase + colOf[x];
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      sum[cell * 3] += SRGB_TO_LINEAR[r];
      sum[cell * 3 + 1] += SRGB_TO_LINEAR[g];
      sum[cell * 3 + 2] += SRGB_TO_LINEAR[b];
      count[cell] += 1;
      if (r >= 250 || g >= 250 || b >= 250) clipped[cell] += 1;
    }
  }
  const grid: Array<Cell | null> = new Array(cols * rows).fill(null);
  const cells: Cell[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const cell = row * cols + col;
      const n = count[cell];
      if (!n) continue;
      const rgb: Rgb = [sum[cell * 3] / n, sum[cell * 3 + 1] / n, sum[cell * 3 + 2] / n];
      const v = (win.top + (row + 0.5) * size - by0) / bh;
      const c: Cell = {
        row,
        col,
        u: (win.left + (col + 0.5) * size - bx0) / bw,
        v,
        rgb,
        lab: linearToLab(rgb[0], rgb[1], rgb[2]),
        n,
        clippedShare: clipped[cell] / n,
        clipped: clipped[cell] * 2 > n,
        border: row === 0 || ((col === 0 || col === cols - 1) && v < COLOUR.borderMaxV),
      };
      grid[cell] = c;
      cells.push(c);
    }
  }
  return { cells, grid, cols, rows };
}

const inRegion = (cell: Cell, regions: readonly Region[]) =>
  regions.some(([u0, v0, u1, v1]) => cell.u >= u0 && cell.u < u1 && cell.v >= v0 && cell.v < v1);
/** Inside the centred ellipse of radius r (box units). */
const inEllipse = (cell: Cell, r: number) => ((cell.u - 0.5) / r) ** 2 + ((cell.v - 0.5) / r) ** 2 <= 1;

function skinGate(lab: Lab): boolean {
  const g = COLOUR.skinGate;
  const h = hue(lab);
  const c = chroma(lab);
  return (
    lab[0] >= g.minL &&
    lab[0] <= g.maxL &&
    lab[1] >= g.minA &&
    lab[2] >= g.minB &&
    h >= g.minHue &&
    h <= g.maxHue &&
    c >= g.minChroma &&
    c <= g.maxChroma
  );
}

/** A cell's luminance against its four neighbours' mean, relative (NaN at the grid's edge). */
function localContrast(sampled: Sampled, c: Cell): number {
  let sum = 0;
  for (const [dr, dc] of [
    [-1, 0],
    [1, 0],
    [0, -1],
    [0, 1],
  ]) {
    const r = c.row + dr;
    const k = c.col + dc;
    const neighbour = r >= 0 && r < sampled.rows && k >= 0 && k < sampled.cols ? sampled.grid[r * sampled.cols + k] : null;
    if (!neighbour) return NaN;
    sum += luminance(neighbour.rgb);
  }
  const mean = sum / 4;
  return Math.abs(luminance(c.rgb) - mean) / Math.max(mean, 0.002);
}

/** The median local contrast of some cells. */
function roughness(sampled: Sampled, cells: Cell[]): number {
  return median(cells.map((c) => localContrast(sampled, c)).filter(Number.isFinite));
}

/**
 * Where the skin starts, going up each column across the forehead (u 0.25 to
 * 0.75): the row, and the colour jump (ΔE) from the first skin cell to three
 * cells above it. A hat's brim is a straight line with a big jump in nearly
 * every column. Null with fewer than three columns.
 */
function foreheadEdge(sampled: Sampled, skinRaw: Lab): { columns: number; residual: number; jump: number } | null {
  const us: number[] = [];
  const vs: number[] = [];
  let jump = 0;
  let columns = 0;
  for (let col = 0; col < sampled.cols; col += 1) {
    const column: Cell[] = [];
    for (let row = 0; row < sampled.rows; row += 1) {
      const c = sampled.grid[row * sampled.cols + col];
      if (c) column.push(c);
    }
    if (!column.length || column[0].u < 0.25 || column[0].u > 0.75) continue;
    columns += 1;
    let i = column.findIndex((c) => c.v >= 0.15);
    if (i < 0) continue;
    while (i > 0 && skinGate(column[i - 1].lab) && deltaE(column[i - 1].lab, skinRaw) < 20) i -= 1;
    if (i < 3 || column[i].v < -0.3) continue;
    jump += deltaE(column[i].lab, column[i - 3].lab);
    us.push(column[i].u);
    vs.push(column[i].v);
  }
  const n = us.length;
  if (n < 3) return null;
  const mu = us.reduce((a, b) => a + b, 0) / n;
  const mv = vs.reduce((a, b) => a + b, 0) / n;
  let sxy = 0;
  let sxx = 0;
  for (let i = 0; i < n; i += 1) {
    sxy += (us[i] - mu) * (vs[i] - mv);
    sxx += (us[i] - mu) ** 2;
  }
  const slope = sxx ? sxy / sxx : 0;
  const residual = Math.sqrt(vs.reduce((acc, v, i) => acc + (v - (mv + slope * (us[i] - mu))) ** 2, 0) / n);
  return { columns: n / Math.max(1, columns), residual, jump: jump / n };
}

// ---------------------------------------------------------------------------
// Naming
// ---------------------------------------------------------------------------

interface Named {
  name: string;
  distance: number;
}

/** The chips in `names` by distance. */
function rank(
  lab: Lab,
  names: readonly string[],
  references: Readonly<Record<string, Readonly<Lab>>>,
  distance: (p: Readonly<Lab>, q: Readonly<Lab>) => number,
): { best: Named; next: Named | null } {
  const out = names.map((name) => ({ name, distance: distance(lab, references[name]) })).sort((p, q) => p.distance - q.distance);
  return { best: out[0], next: out[1] || null };
}

const rankSkin = (lab: Lab) => rank(lab, STORYBOOK_SKIN_TONES, SKIN_REFERENCES, deltaE94);

/** The skin chip nearest a Lab colour (CIE94). */
export function nameSkin(lab: Lab): Named {
  return rankSkin(lab).best;
}

/** The hair chips a colour may be: gray/white only when low in chroma and light; the warm ones and blonde by hue. */
function hairCandidates(lab: Lab): string[] {
  const g = COLOUR.hairGates;
  const c = chroma(lab);
  const h = hue(lab);
  if (c < g.greyMaxChroma && lab[0] >= g.greyMinL) return ['gray', 'white'];
  const out = ['black', 'dark brown', 'brown', 'light brown'];
  if (lab[0] >= g.blondeMinL && h >= g.blondeMinHue && c >= g.blondeMinChroma) out.push('blonde');
  if (lab[1] >= g.warmMinA && h <= g.warmMaxHue && c >= g.warmMinChroma) out.push('strawberry blonde', 'red', 'auburn');
  return out;
}

const rankHair = (lab: Lab) => rank(lab, hairCandidates(lab), HAIR_REFERENCES, deltaE);

/** The hair chip nearest a Lab colour among the ones it may be (CIE76). */
export function nameHair(lab: Lab): Named {
  return rankHair(lab).best;
}

/** The least of closeness, clearance from the runner-up, and support. */
function confidenceOf(ranked: { best: Named; next: Named | null }, maxDistance: number, cells: number, minCells: number): number {
  const close = clamp01(1 - ranked.best.distance / maxDistance);
  const clear = ranked.next ? clamp01(1 - ranked.best.distance / Math.max(ranked.next.distance, 1e-9)) : 1;
  const support = clamp01(cells / (minCells * COLOUR.fullSupport));
  return round(Math.min(close, clear, support));
}

// ---------------------------------------------------------------------------
// The estimate
// ---------------------------------------------------------------------------

export type StorybookColourField = 'skinTone' | 'hairColor';

/** Who the face is, to the estimator: a child (gray or white hair needs COLOUR.childGreyConfidence), a bald look (no hair read). */
export interface StorybookColourPerson {
  child?: boolean;
  bald?: boolean;
}

export interface StorybookColourEstimate {
  /** A skin tone chip (config.appearance.skinTone), or null when unsure. */
  skinTone: string | null;
  /** A hair colour chip (config.appearance.hairColor), or null when unsure. */
  hairColor: string | null;
  /** 0..1 per field (0 for a null field). */
  confidence: { skinTone: number; hairColor: number };
  /** Why a field is null: a code (face_at_edge, too_dark, colour_cast, scalp, hat, …), never a colour. */
  reason?: Partial<Record<StorybookColourField, string>>;
  /** The measurements (details: true only). */
  details?: Record<string, unknown>;
}

function validBox(box: StorybookFaceBox, width: number, height: number): boolean {
  return (
    Array.isArray(box) &&
    box.length === 4 &&
    box.every((v) => Number.isFinite(v)) &&
    box[2] > box[0] &&
    box[3] > box[1] &&
    width > 0 &&
    height > 0
  );
}

interface FieldAnswer {
  name?: string;
  confidence?: number;
  reason?: string;
}

const FIELDS: readonly StorybookColourField[] = ['skinTone', 'hairColor'];

/**
 * The skin tone and hair colour of the face in `box` (normalized to `image`),
 * as chip names, each null when unsure. Steps 1-8 read the box once; step 9
 * keeps a chip only when the box scaled by COLOUR.stability.scales agrees
 * ('unstable' when it does not).
 */
export function estimateColours(
  image: StorybookColourImage,
  box: StorybookFaceBox,
  { details = false, person = {} }: { details?: boolean; person?: StorybookColourPerson | null } = {},
): StorybookColourEstimate {
  const main = readOnce(image, box, { details, person });
  if (!main.skinTone && !main.hairColor) return main;

  // 9. Box stability.
  const again = COLOUR.stability.scales.map((s) => readOnce(image, scaleBox(box, s), { person }));
  const out: StorybookColourEstimate = { skinTone: main.skinTone, hairColor: main.hairColor, confidence: { ...main.confidence } };
  const why: Partial<Record<StorybookColourField, string>> = { ...(main.reason || {}) };
  for (const field of FIELDS) {
    if (!main[field]) continue;
    const same = again.filter((r) => r[field] === main[field]).length;
    const other = again.some((r) => r[field] && r[field] !== main[field]);
    if (!other && same >= COLOUR.stability.minAgree) continue;
    out[field] = null;
    out.confidence[field] = 0;
    why[field] = 'unstable';
  }
  const reason: Partial<Record<StorybookColourField, string>> = {};
  for (const field of FIELDS) if (why[field]) reason[field] = why[field];
  if (Object.keys(reason).length) out.reason = reason;
  if (main.details) out.details = { ...main.details, stability: again.map((r) => [r.skinTone, r.hairColor]) };
  return out;
}

/** Steps 1-8: one read of one box. */
function readOnce(
  image: StorybookColourImage,
  box: StorybookFaceBox,
  { details = false, person = {} }: { details?: boolean; person?: StorybookColourPerson | null } = {},
): StorybookColourEstimate {
  const det: Record<string, unknown> | null = details ? {} : null;
  const answer = (skin: FieldAnswer, hair: FieldAnswer): StorybookColourEstimate => {
    const out: StorybookColourEstimate = {
      skinTone: skin.name || null,
      hairColor: hair.name || null,
      confidence: { skinTone: skin.name ? skin.confidence : 0, hairColor: hair.name ? hair.confidence : 0 },
    };
    const reason: Partial<Record<StorybookColourField, string>> = {};
    if (!skin.name) reason.skinTone = skin.reason;
    if (!hair.name) reason.hairColor = hair.reason;
    if (Object.keys(reason).length) out.reason = reason;
    if (det) out.details = det;
    return out;
  };
  const refuse = (reason: string) => answer({ reason }, { reason });
  const who = person || {};

  // 1. A face, whole, big enough.
  if (!image || !image.data || (image.channels || 4) < 3 || !validBox(box, image.width, image.height)) return refuse('unreadable');
  const W = image.width;
  const H = image.height;
  const bw = (box[2] - box[0]) * W;
  const bh = (box[3] - box[1]) * H;
  if (Math.min(bw, bh) < COLOUR.minFacePx - 0.5) return refuse('face_too_small');
  const m = COLOUR.edgeMargin;
  if (box[0] * W < m * bw || W - box[2] * W < m * bw || box[1] * H < m * bh || H - box[3] * H < m * bh) return refuse('face_at_edge');

  // 2, 3. Cells; the skin.
  const sampled = sampleCells(image, box);
  const { cells } = sampled;
  const region = cells.filter((c) => inRegion(c, COLOUR.skinRegions));
  const gated = region.filter((c) => !c.clipped && skinGate(c.lab));
  if (det) det.skinShare = round(gated.length / Math.max(1, region.length));
  if (gated.length < COLOUR.minSkinCells || gated.length < region.length * COLOUR.minSkinShare) return refuse('no_skin');
  const lo = quantile(gated.map((c) => c.lab[0]), COLOUR.litBand[0]);
  const hi = quantile(gated.map((c) => c.lab[0]), COLOUR.litBand[1]);
  const lit = gated.filter((c) => c.lab[0] >= lo && c.lab[0] <= hi);
  const skinRaw = medianLab(lit);
  const parts = COLOUR.skinRegions.map((r) => gated.filter((c) => inRegion(c, [r])));
  if (parts.some((p) => p.length < 5)) return refuse('no_skin');
  const partL = parts.map((p) => median(p.map((c) => c.lab[0])));
  const partHue = parts.map((p) => hue(medianLab(p)));
  const eyesL = quantile(cells.filter((c) => inRegion(c, [COLOUR.eyeBand])).map((c) => c.lab[0]), 0.1);
  if (det) Object.assign(det, { skinRaw: skinRaw.map((v) => round(v, 1)), partL: partL.map((v) => round(v, 1)), partHue: partHue.map((v) => round(v, 0)), eyesL: round(eyesL, 1) });
  const f = COLOUR.face;
  if (!(eyesL < skinRaw[0] - f.eyesDarker)) return refuse('not_a_face');
  if (Math.max(hueGap(partHue[0], partHue[1]), hueGap(partHue[0], partHue[2]), hueGap(partHue[1], partHue[2])) > f.partsHueGap) return refuse('not_a_face');
  if (Math.max(...partL) - Math.min(...partL) > f.partsSpread) return refuse('not_a_face');

  // 4. Light.
  const oval = cells.filter((c) => inEllipse(c, COLOUR.faceOval));
  const highlight = quantile(oval.map((c) => c.lab[0]), COLOUR.exposure.percentile);
  const clippedShare = region.reduce((s, c) => s + c.clippedShare * c.n, 0) / region.reduce((s, c) => s + c.n, 0);
  const around = cells.filter((c) => !c.clipped && !inEllipse(c, COLOUR.faceEllipse));
  const aroundL = quantile(around.map((c) => c.lab[0]), COLOUR.shade.outsideQuantile);
  const k = (lightnessToY(COLOUR.exposure.target) / lightnessToY(highlight)) ** COLOUR.exposure.strength;
  if (det) Object.assign(det, { highlight: round(highlight, 1), around: round(aroundL, 1), clippedShare: round(clippedShare), exposure: round(k, 3) });
  if (skinRaw[0] > COLOUR.blownOut.maxSkinL || clippedShare > COLOUR.blownOut.clippedShare) return refuse('too_bright');
  const small = Math.min(bw, bh) < COLOUR.exposure.smallFacePx;
  const stops = Math.log2(k);
  if (!(stops <= (small ? COLOUR.exposure.smallStops : COLOUR.exposure.brighten))) return refuse('too_dark');
  if (!(stops >= -(small ? COLOUR.exposure.smallStops : COLOUR.exposure.darken))) return refuse('too_bright');
  if (aroundL - highlight > COLOUR.shade.maxGap) return refuse('face_in_shade');
  if (partL[0] < Math.min(partL[1], partL[2]) - COLOUR.shade.foreheadShadow) return refuse('face_in_shade');

  // 5. White balance.
  const cast = COLOUR.cast;
  const refL = Math.max(cast.minL, quantile(around.map((c) => c.lab[0]), 1 - cast.share));
  const refCells = around.filter((c) => c.lab[0] >= refL);
  let castChroma: number;
  if (refCells.length >= cast.minCells) {
    const ref = medianLab(refCells);
    castChroma = chroma(ref);
    if (det) det.cast = { by: 'bright', a: round(ref[1], 1), b: round(ref[2], 1) };
    if (castChroma > cast.maxChroma) return refuse('colour_cast');
  } else {
    const total = cells.reduce((s, c) => s + c.n, 0);
    const mean = [0, 1, 2].map((j) => cells.reduce((s, c) => s + c.rgb[j] * c.n, 0) / total);
    const grey = linearToLab(mean[0], mean[1], mean[2]);
    castChroma = chroma(grey);
    if (det) det.cast = { by: 'grey world', a: round(grey[1], 1), b: round(grey[2], 1) };
    if (castChroma > cast.grayWorldMaxChroma) return refuse('colour_cast');
  }

  // 6. The skin chip.
  const skinLab = exposeLab(skinRaw, k);
  const skin: FieldAnswer = {};
  const sr = rankSkin(skinLab);
  skin.confidence = confidenceOf(sr, COLOUR.skinMaxDistance, gated.length, COLOUR.minSkinCells);
  if (det) Object.assign(det, { skin: skinLab.map((v) => round(v, 1)), skinRank: [sr.best.name, round(sr.best.distance, 2), sr.next.name, round(sr.next.distance, 2)] });
  if (skin.confidence >= STORYBOOK_COLOUR_PICK_CONFIDENCE) skin.name = sr.best.name;
  else skin.reason = sr.best.distance > (1 - STORYBOOK_COLOUR_PICK_CONFIDENCE) * COLOUR.skinMaxDistance ? 'no_match' : 'between';

  // 7. Hair.
  const hair: FieldAnswer = {};
  hair.reason = ((): string | undefined => {
    if (who.bald) return 'bald';
    const room = COLOUR.hairRoom;
    if (box[1] * H < room.top * bh || box[0] * W < room.side * bw || W - box[2] * W < room.side * bw) return 'hair_cut_off';
    const border = medianLab(cells.filter((c) => c.border));
    const skinChroma = chroma(skinRaw);
    const skinHue = hue(skinRaw);
    const t = COLOUR.hairVsSkin;
    const isHair = (c: Cell) => {
      if (deltaE(c.lab, border) < COLOUR.backgroundDistance || deltaE(c.lab, skinRaw) < t.minDistance) return false;
      const cc = chroma(c.lab);
      return (
        c.lab[0] < skinRaw[0] - t.darker ||
        cc > skinChroma + t.moreChroma ||
        (cc >= t.hueMinChroma && hueGap(hue(c.lab), skinHue) > t.hueTurn) ||
        (cc < t.greyChroma && skinChroma - cc > t.greyerBy)
      );
    };
    const hairCells = cells.filter((c) => inRegion(c, COLOUR.hairRegions) && isHair(c));
    const top = cells.filter((c) => inRegion(c, [COLOUR.hairTop]));
    const topHair = top.filter(isHair);
    const cover = top.length ? topHair.length / top.length : 0;
    if (det) Object.assign(det, { hairCells: hairCells.length, hairCover: round(cover) });
    if (hairCells.length < COLOUR.minHairCells) return 'no_hair';
    if (cover < COLOUR.minHairCover) return 'sparse';
    const hairRaw = medianLab(hairCells);
    const skinRgb = medianRgb(lit);
    const hairRgb = medianRgb(hairCells);
    const cs = chromaticity(skinRgb);
    const ch = chromaticity(hairRgb);
    const chromaGap = Math.hypot(ch[0] - cs[0], ch[1] - cs[1], ch[2] - cs[2]);
    const yRatio = luminance(hairRgb) / luminance(skinRgb);
    const texture = roughness(sampled, topHair) / Math.max(roughness(sampled, lit), 0.004);
    if (det) Object.assign(det, { hairRaw: hairRaw.map((v) => round(v, 1)), chromaGap: round(chromaGap, 3), yRatio: round(yRatio, 3), texture: round(texture, 2) });
    if (chromaGap < COLOUR.scalp.maxChromaticity && yRatio > COLOUR.scalp.minYRatio) return 'scalp';
    if (!(texture >= COLOUR.texture.minRatio)) return 'not_hair';
    const edge = foreheadEdge(sampled, skinRaw);
    if (det && edge) det.foreheadEdge = { columns: round(edge.columns), residual: round(edge.residual, 3), jump: round(edge.jump, 1) };
    const h = COLOUR.hat;
    if (edge && edge.columns >= h.minColumns && edge.residual <= h.maxResidual && edge.jump >= h.minJump && texture < h.maxTexture) return 'hat';
    const hairLab = exposeLab(hairRaw, k);
    const hr = rankHair(hairLab);
    hair.confidence = confidenceOf(hr, COLOUR.hairMaxDistance, hairCells.length, COLOUR.minHairCells);
    if (det) Object.assign(det, { hair: hairLab.map((v) => round(v, 1)), hairRank: [hr.best.name, round(hr.best.distance, 1), hr.next && hr.next.name, hr.next && round(hr.next.distance, 1)] });
    const grey = hr.best.name === 'gray' || hr.best.name === 'white';
    if (grey && who.child && hair.confidence < COLOUR.childGreyConfidence) return 'grey_on_child';
    if (hair.confidence < STORYBOOK_COLOUR_PICK_CONFIDENCE) return hr.best.distance > (1 - STORYBOOK_COLOUR_PICK_CONFIDENCE) * COLOUR.hairMaxDistance ? 'no_match' : 'between';
    hair.name = hr.best.name;
    return undefined;
  })();
  return answer(skin, hair);
}

// ---------------------------------------------------------------------------
// Suggesting a person's colour chips from their photo (the builder)
// ---------------------------------------------------------------------------

export const STORYBOOK_COLOUR_FIELDS: readonly StorybookColourField[] = ['skinTone', 'hairColor'];

/** A photo's answer as the builder keeps it: the chip per field, and how sure. */
export type StorybookPhotoRead = Pick<StorybookColourEstimate, 'skinTone' | 'hairColor'> & Partial<Pick<StorybookColourEstimate, 'confidence'>>;

/** What a photo suggests for one person: a chip per field it is sure of. */
export type StorybookPhotoSuggestion = Partial<Record<StorybookColourField, string>>;

/** Who a builder person is to the estimator: a child, a bald look. */
export function storybookColourPerson(look: { hairStyle?: string }, child: boolean): StorybookColourPerson {
  return { child, bald: isBaldHairStyle(look.hairStyle) };
}

/**
 * The chip a photo's answer gives a field, or '' for none: a chip offered by
 * the builder (`allowed`, when given), named at the pick confidence (when a
 * confidence comes with it).
 */
export function photoChip(read: StorybookPhotoRead | null | undefined, field: StorybookColourField, allowed?: readonly string[]): string {
  const value = read?.[field];
  if (!value) return '';
  if (allowed && !allowed.includes(value)) return '';
  if (read.confidence && !(Number(read.confidence[field]) >= STORYBOOK_COLOUR_PICK_CONFIDENCE)) return '';
  return value;
}

/**
 * What a person's photo suggests for their skin tone / hair colour chips:
 * only chips the builder offers (`allowed`), named at the pick confidence,
 * that differ from what the look shows now (an empty chip included); never a
 * bald head's hair colour. {} when there is nothing to suggest. A suggestion
 * is only ever shown: the parent applies it with a click, or not.
 */
export function photoSuggestion(
  look: { skinTone: string; hairColor: string; hairStyle: string },
  read: StorybookPhotoRead | null | undefined,
  allowed?: Partial<Record<StorybookColourField, readonly string[]>>,
): StorybookPhotoSuggestion {
  const out: StorybookPhotoSuggestion = {};
  for (const field of STORYBOOK_COLOUR_FIELDS) {
    if (field === 'hairColor' && isBaldHairStyle(look.hairStyle)) continue;
    const chip = photoChip(read, field, allowed?.[field]);
    if (chip && chip !== look[field]) out[field] = chip;
  }
  return out;
}

// ---------------------------------------------------------------------------
// In the browser: a prepared photo (storybook-photo.ts) and a tagged face
// ---------------------------------------------------------------------------

/** A photo that has not decoded by then is not read (a data URL decodes in milliseconds). */
export const STORYBOOK_COLOUR_TIMEOUT_MS = 10_000;

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => reject(new Error('decode_timeout')), STORYBOOK_COLOUR_TIMEOUT_MS);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error('decode_failed'));
    };
    img.src = dataUrl;
  });
}

/**
 * The colours of one tagged face in a prepared photo (a JPEG data URL, the
 * exact bytes the server gets; `box` the face as the payload sends it). Only
 * the analysis window is drawn, at the photo's own size, so the numbers are
 * the Node twin's on the same bytes. Null when the browser can't read the
 * photo; never throws.
 */
export async function estimateStorybookFaceColours(
  dataUrl: string,
  box: StorybookFaceBox,
  person?: StorybookColourPerson,
): Promise<StorybookColourEstimate | null> {
  try {
    const img = await loadImage(dataUrl);
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height || !validBox(box, width, height)) return null;
    const win = analysisWindow(box, width, height);
    if (!win.width || !win.height) return null;
    const canvas = document.createElement('canvas');
    canvas.width = win.width;
    canvas.height = win.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;
    if (!ctx) return null;
    ctx.drawImage(img, win.left, win.top, win.width, win.height, 0, 0, win.width, win.height);
    const pixels = ctx.getImageData(0, 0, win.width, win.height);
    canvas.width = 0;
    canvas.height = 0;
    return estimateColours({ data: pixels.data, width: win.width, height: win.height, channels: 4 }, boxInWindow(box, width, height, win), { person });
  } catch {
    return null;
  }
}
